/**
 * Flood fill (engine 46.3 "Fill"). Scanline, iterative (never recursive), and
 * **fenced** to the painted-chunk bounds expanded by one chunk on each side so
 * a click on the infinite empty void can't run away. Fills the 4-connected
 * region of cells sharing the clicked cell's tile id, writing the stamp's
 * top-left tile (single-tile fill) — multi-tile stamp fill tiles the stamp
 * across the region (see `fillRegion`'s `stampAt`).
 *
 * Pure over LayerTiles + Stroke, so it unit-tests without a canvas.
 */

import { CHUNK_SIZE, EMPTY_TILE, cellToChunk, type TileID } from '../../model/constants';
import type { LayerTiles } from '../../model/chunk';
import type { Stroke } from '../../store/strokes';
import type { Stamp } from '../../model/types';

interface Bounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/**
 * The fill fence: the bounding box of painted chunks, expanded by one chunk on
 * every side, expressed in cells. Guarantees a void-click fills at most a
 * one-chunk apron around existing content (or, on a totally empty layer, the
 * single chunk containing the click — see caller).
 */
function fenceBounds(tiles: LayerTiles, clickX: number, clickY: number): Bounds {
    let has = false;
    let cxMin = 0, cyMin = 0, cxMax = 0, cyMax = 0;
    for (const c of tiles.allChunks()) {
        if (!has) {
            cxMin = cxMax = c.cx;
            cyMin = cyMax = c.cy;
            has = true;
        } else {
            cxMin = Math.min(cxMin, c.cx); cxMax = Math.max(cxMax, c.cx);
            cyMin = Math.min(cyMin, c.cy); cyMax = Math.max(cyMax, c.cy);
        }
    }

    if (!has) {
        // Empty layer: fence to the single chunk under the click.
        const cx = cellToChunk(clickX);
        const cy = cellToChunk(clickY);
        return {
            minX: cx * CHUNK_SIZE, minY: cy * CHUNK_SIZE,
            maxX: (cx + 1) * CHUNK_SIZE - 1, maxY: (cy + 1) * CHUNK_SIZE - 1,
        };
    }

    // Expand by one chunk on each side (engine's "± 1 chunk").
    return {
        minX: (cxMin - 1) * CHUNK_SIZE,
        minY: (cyMin - 1) * CHUNK_SIZE,
        maxX: (cxMax + 2) * CHUNK_SIZE - 1,
        maxY: (cyMax + 2) * CHUNK_SIZE - 1,
    };
}

/**
 * Flood fill from (clickX, clickY). Returns true if anything changed. The
 * stamp is tiled across the filled region anchored at the fence origin so a
 * multi-tile stamp produces a coherent pattern; a 1×1 stamp is a plain fill.
 */
export function fillRegion(
    tiles: LayerTiles,
    stroke: Stroke,
    clickX: number,
    clickY: number,
    stamp: Stamp,
): boolean {
    const target = tiles.getTile(clickX, clickY);

    // The value we'd write at the click. If it equals target, nothing to do
    // (filling a region with its own tile is a no-op) — but only when the
    // stamp is 1×1; a multi-tile stamp can still meaningfully change cells.
    const single = stamp.w === 1 && stamp.h === 1;
    const singleId = stamp.tiles[0] ?? EMPTY_TILE;
    if (single && singleId === target) return false;

    const bounds = fenceBounds(tiles, clickX, clickY);
    const stampAt = (x: number, y: number): TileID => {
        if (single) return singleId;
        // Tile the stamp across the region using fence-origin anchoring; row 0
        // (top) maps to higher world Y, matching paintStamp.
        const c = ((x - bounds.minX) % stamp.w + stamp.w) % stamp.w;
        const r = ((bounds.maxY - y) % stamp.h + stamp.h) % stamp.h;
        return stamp.tiles[r * stamp.w + c] ?? EMPTY_TILE;
    };

    let changed = false;
    const stack: Array<[number, number]> = [[clickX, clickY]];
    const seen = new Set<string>();

    while (stack.length) {
        const [x, y] = stack.pop()!;
        if (x < bounds.minX || x > bounds.maxX || y < bounds.minY || y > bounds.maxY) continue;
        const key = `${x},${y}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (tiles.getTile(x, y) !== target) continue;

        const id = stampAt(x, y);
        if (id !== target) {
            const before = tiles.getTile(x, y);
            if (tiles.setTile(x, y, id)) {
                stroke.record(x, y, before, id);
                changed = true;
            }
        }

        stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    return changed;
}
